import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Square, Play, Pause, RotateCcw, Upload, Loader2 } from 'lucide-react';

interface VoiceRecorderProps {
  onRecordingComplete: (blob: Blob) => Promise<void>;
  disabled?: boolean;
}

export function VoiceRecorder({ onRecordingComplete, disabled = false }: VoiceRecorderProps) {
  const [state, setState] = useState<'idle' | 'recording' | 'recorded' | 'previewing' | 'uploading'>('idle');
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const recordedBlobRef = useRef<Blob | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number>();

  const cleanupStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      cleanupStream();
      if (timerRef.current) clearInterval(timerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      previewAudioRef.current?.pause();
    };
  }, [cleanupStream]);

  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);

      ctx.fillStyle = 'rgb(15, 23, 42)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgb(168, 85, 247)';
      ctx.beginPath();

      const sliceWidth = canvas.width / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * canvas.height) / 2;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        x += sliceWidth;
      }

      ctx.lineTo(canvas.width, canvas.height / 2);
      ctx.stroke();
    };

    draw();
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    setPermissionDenied(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        recordedBlobRef.current = blob;
        cleanupStream();
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        setState('recorded');
      };

      mediaRecorderRef.current = recorder;
      recorder.start(250);

      setDuration(0);
      timerRef.current = setInterval(() => {
        setDuration((d) => d + 1);
      }, 1000);

      setState('recording');
      drawWaveform();
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionDenied(true);
      } else {
        setError('Could not access microphone. Please check your browser permissions.');
      }
    }
  }, [cleanupStream, drawWaveform]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    mediaRecorderRef.current?.stop();
  }, []);

  const togglePreview = useCallback(() => {
    if (!recordedBlobRef.current) return;

    if (state === 'previewing') {
      previewAudioRef.current?.pause();
      setState('recorded');
      return;
    }

    const url = URL.createObjectURL(recordedBlobRef.current);
    const audio = new Audio(url);
    audio.onended = () => {
      setState('recorded');
      URL.revokeObjectURL(url);
    };
    audio.play().catch(() => {});
    previewAudioRef.current = audio;
    setState('previewing');
  }, [state]);

  const resetRecording = useCallback(() => {
    previewAudioRef.current?.pause();
    recordedBlobRef.current = null;
    setDuration(0);
    setState('idle');
  }, []);

  const handleUpload = useCallback(async () => {
    if (!recordedBlobRef.current) return;
    setState('uploading');
    try {
      await onRecordingComplete(recordedBlobRef.current);
      recordedBlobRef.current = null;
      setDuration(0);
      setState('idle');
    } catch {
      setError('Failed to save recording. Please try again.');
      setState('recorded');
    }
  }, [onRecordingComplete]);

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (permissionDenied) {
    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
        <p className="text-xs text-red-400 mb-2">Microphone access denied</p>
        <p className="text-[10px] text-slate-500">
          Please allow microphone access in your browser settings and refresh the page.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Mic size={14} className={state === 'recording' ? 'text-red-400 animate-pulse' : 'text-blue-400'} />
        <span className="text-xs font-medium text-white">Record Voiceover</span>
      </div>

      {state === 'recording' && (
        <canvas ref={canvasRef} width={300} height={40} className="w-full h-10 rounded bg-slate-900" />
      )}

      <div className="flex items-center gap-2">
        {state === 'idle' && (
          <button
            onClick={startRecording}
            disabled={disabled}
            className="flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
          >
            <Mic size={12} />
            Start Recording
          </button>
        )}

        {state === 'recording' && (
          <>
            <button
              onClick={stopRecording}
              className="flex items-center gap-1.5 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <Square size={12} />
              Stop
            </button>
            <span className="text-xs font-mono text-red-400 animate-pulse">
              {formatDuration(duration)}
            </span>
          </>
        )}

        {(state === 'recorded' || state === 'previewing') && (
          <>
            <button
              onClick={togglePreview}
              className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium rounded-lg transition-colors"
            >
              {state === 'previewing' ? <Pause size={12} /> : <Play size={12} />}
              {state === 'previewing' ? 'Pause' : 'Preview'}
            </button>
            <button
              onClick={resetRecording}
              className="flex items-center gap-1.5 px-2 py-2 text-slate-400 hover:text-white text-xs transition-colors"
              title="Re-record"
            >
              <RotateCcw size={12} />
            </button>
            <button
              onClick={handleUpload}
              className="flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-500 text-white text-xs font-medium rounded-lg transition-colors ml-auto"
            >
              <Upload size={12} />
              Save
            </button>
            <span className="text-[10px] font-mono text-slate-500">{formatDuration(duration)}</span>
          </>
        )}

        {state === 'uploading' && (
          <div className="flex items-center gap-2">
            <Loader2 size={14} className="animate-spin text-purple-400" />
            <span className="text-xs text-slate-400">Saving recording...</span>
          </div>
        )}
      </div>

      {error && <p className="text-[10px] text-red-400">{error}</p>}
    </div>
  );
}
